import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, DollarSign, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const CHART_TOOLTIP_STYLE = { background: "#18181b", border: "1px solid #27272a", borderRadius: 4 };
const CHART_LABEL_STYLE = { color: "#fafafa", fontWeight: 600 };
const CHART_ITEM_STYLE = { color: "#fafafa" };
const PIE_COLORS = ["#f59e0b", "#3b82f6", "#10b981", "#8b5cf6", "#ef4444"];
const TICK_STYLE = { fontSize: 11 };

export function RevenueCharts({ monthlyData, clientData, gearData, show }) {
  const s = { monthly: true, client: true, gear: false, ...(show || {}) };
  if (!s.monthly && !s.client && !s.gear) return null;
  const topGear = (gearData || []).slice(0, 8);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {s.monthly && (
      <Card className={`${s.client ? "lg:col-span-7" : "lg:col-span-12"} border-border`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Monthly Revenue
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="monthly-revenue-chart">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" />
              <XAxis dataKey="month" tick={TICK_STYLE} tickLine={false} axisLine={false} />
              <YAxis tick={TICK_STYLE} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#f59e0b" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      )}

      {s.client && (
      <Card className={`${s.monthly ? "lg:col-span-5" : "lg:col-span-12"} border-border`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Revenue by Client
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="client-revenue-chart">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={clientData} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={90} strokeWidth={1} stroke="#09090b">
                {clientData.map((d) => <Cell key={d.name} fill={PIE_COLORS[clientData.indexOf(d) % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} itemStyle={CHART_ITEM_STYLE} formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {clientData.map((c, i) => (
              <div key={c.name} className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="text-muted-foreground">{c.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      {s.gear && (
      <Card className="lg:col-span-12 border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Package className="w-4 h-4" /> Revenue by Gear
            <span className="text-xs font-normal normal-case text-muted-foreground/60 ml-1">Event revenue split across assigned gear</span>
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="gear-revenue-chart">
          {topGear.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No gear revenue in this date range</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(140, topGear.length * 36)}>
              <BarChart data={topGear} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" horizontal={false} />
                <XAxis type="number" tick={TICK_STYLE} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" tick={TICK_STYLE} width={140} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} />
                <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
